import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Firebase } from '../../../firebase/config';
import { verificationsRef, serverTimestamp } from '../../../firebase/collections';
import { COOLDOWN_DAYS } from '../../../constants';
import { silentCatch } from '../../../utils/errorHandler';

const MAX_ID_SIZE = 5 * 1024 * 1024;

export function useVerification(user, userName, verificationDoc, vStatus, isResolved, cooldownActive, daysRemaining, addToast) {
  const { t } = useTranslation();
  const verificationFileRef = useRef(null);
  const [verificationReason, setVerificationReason] = useState('');
  const [verificationIdFile, setVerificationIdFile] = useState(null);
  const [verificationIdPreview, setVerificationIdPreview] = useState(null);
  const [submittingVerification, setSubmittingVerification] = useState(false);

  const handleVerificationIdSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast(t('profile.pleaseSelectIdImage'), 'error');
      return;
    }
    if (file.size > MAX_ID_SIZE) {
      addToast(t('profile.idImageMustBeLess'), 'error');
      return;
    }
    setVerificationIdFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setVerificationIdPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!user?.uid || submittingVerification) return;
    if (!verificationReason.trim()) {
      addToast(t('profile.pleaseProvideReason'), 'error');
      return;
    }
    if (!verificationIdFile) {
      addToast(t('profile.idDocumentRequired'), 'error');
      return;
    }
    if (vStatus === 'pending') {
      addToast(t('profile.pendingRequest'), 'error');
      return;
    }
    if (isResolved && cooldownActive) {
      addToast(
        t('verification.reapplyInDays', { count: daysRemaining }),
        'error'
      );
      return;
    }
    setSubmittingVerification(true);
    try {
      const ext = verificationIdFile.name.split('.').pop();
      const storageRef = Firebase.storage().ref(`profiles/${user.uid}/verification-id.${ext}`);
      await storageRef.put(verificationIdFile);
      const idDocUrl = await storageRef.getDownloadURL();
      await verificationsRef().add({
        userId: user.uid,
        userName: (userName || '').trim() || user.displayName || '',
        userEmail: user.email || '',
        reason: verificationReason.trim(),
        idDocumentUrl: idDocUrl,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      if (verificationDoc) {
        verificationsRef()
          .doc(verificationDoc.id)
          .delete()
          .catch(silentCatch('EditProfile:deleteOldVerification'));
      }
      setVerificationReason('');
      setVerificationIdFile(null);
      setVerificationIdPreview(null);
      if (verificationFileRef.current) verificationFileRef.current.value = '';
      addToast(t('profile.verificationRequestSubmitted'), 'success');
    } catch (err) {
      console.error('Verification request failed:', err);
      addToast(t('profile.failedToSubmitVerification'), 'error');
    }
    setSubmittingVerification(false);
  };

  return {
    verificationReason,
    setVerificationReason,
    verificationIdFile,
    setVerificationIdFile,
    verificationIdPreview,
    setVerificationIdPreview,
    submittingVerification,
    verificationFileRef,
    handleVerificationIdSelect,
    handleVerificationSubmit,
  };
}
